import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  getOAuthRedirectUri,
  getSharedCookie,
  deleteSharedCookie,
} from '../utils/authRedirect';
import { adminPath, navigateOrExternal } from '../utils/subreddit';

/** Avoid duplicate POST /auth/reddit-callback (React Strict Mode runs effects twice; Reddit codes are single-use). */
const OAUTH_EXCHANGE_PREFIX = 'reddit_oauth_exchange_';

function formatLoginHttpError(
  status: number,
  errorBody: Record<string, unknown>
): string {
  const msg = errorBody.message;
  if (typeof msg === 'string' && msg.trim()) return msg;
  const details = errorBody.details;
  if (typeof details === 'string' && details.trim()) return details;
  const err = errorBody.error;
  if (typeof err === 'string' && err.trim() && err !== String(status)) return err;
  if (status === 404) {
    return 'Login service not found. Check API / proxy configuration.';
  }
  return `Login failed (${status}). Check Reddit app redirect URI matches: ${getOAuthRedirectUri()}`;
}

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      // Check for OAuth errors
      if (error) {
        setError(`OAuth error: ${error}`);
        return;
      }

      // Check for authorization code
      if (!code) {
        setError('No authorization code received');
        return;
      }

      // Check if this is a flair auth callback (for moderator Reddit authentication).
      // State must be readable on www when OAuth started on *.flairwizard.com — use shared cookie + sessionStorage.
      const flairAuthState =
        sessionStorage.getItem('reddit_flair_auth_state') ||
        getSharedCookie('reddit_flair_auth_state');
      if (flairAuthState && state === flairAuthState) {
        // This is a flair auth callback - extract subreddit from state
        try {
          const decodedState = atob(state);
          const [subreddit] = decodedState.split(':');
          
          if (subreddit) {
            // Get user from localStorage (they should be logged in already)
            const savedUser = localStorage.getItem('reddit_user');
            if (!savedUser) {
              setError('Please log in first before authenticating Reddit flair');
              return;
            }
            
            const user = JSON.parse(savedUser);
            const { API_BASE_URL } = await import('../config');
            
            const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/auth-reddit/callback`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.username}`,
              },
              body: JSON.stringify({
                code: code,
                state: state,
              }),
            });
            
            if (response.ok) {
              // Success - redirect to admin page
              sessionStorage.removeItem('reddit_flair_auth_state');
              deleteSharedCookie('reddit_flair_auth_state');
              navigateOrExternal(navigate, `${adminPath(subreddit)}?auth=success`);
              return;
            } else {
              const errorData = await response.json();
              setError(errorData.error || errorData.details || 'Failed to complete Reddit flair authentication');
              return;
            }
          }
        } catch (e) {
          console.error('Error decoding flair auth state:', e);
          setError('Invalid authentication state');
          return;
        }
      }

      // Regular login callback
      const savedState =
        sessionStorage.getItem('reddit_oauth_state') || getSharedCookie('reddit_oauth_state');
      if (state !== savedState) {
        setError('Invalid state parameter');
        return;
      }

      const exchangeKey = `${OAUTH_EXCHANGE_PREFIX}${code}`;
      const exchangeStatus = sessionStorage.getItem(exchangeKey);
      if (exchangeStatus === 'done') {
        const raw = localStorage.getItem('reddit_user');
        if (raw) {
          try {
            setUser(JSON.parse(raw));
          } catch {
            /* ignore */
          }
        }
        sessionStorage.removeItem('reddit_oauth_state');
        deleteSharedCookie('reddit_oauth_state');
        const postLogin = getSharedCookie('post_login_redirect');
        deleteSharedCookie('post_login_redirect');
        if (postLogin) {
          window.location.href = postLogin;
        } else {
          navigate('/');
        }
        return;
      }
      if (exchangeStatus === 'pending') {
        return;
      }
      sessionStorage.setItem(exchangeKey, 'pending');

      try {
        console.log('OAuth code received:', code);

        const { API_BASE_URL } = await import('../config');

        console.log('API Base URL:', API_BASE_URL);
        console.log('Making request to:', `${API_BASE_URL}/auth/reddit-callback`);

        try {
          const response = await fetch(`${API_BASE_URL}/auth/reddit-callback`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code: code,
              redirect_uri: getOAuthRedirectUri(),
            }),
          });

          console.log('Response status:', response.status);
          console.log('Response ok:', response.ok);

          let errorBody: Record<string, unknown> = {};
          try {
            errorBody = await response.json();
          } catch {
            /* non-JSON body */
          }

          if (response.ok) {
            const userData = errorBody as {
              id?: string;
              username?: string;
              name?: string;
              isModerator?: boolean;
            };
            console.log('User data from backend:', userData);

            const realUser = {
              id: userData.id || `reddit_${Date.now()}`,
              username: String(userData.username ?? userData.name ?? ''),
              isModerator: userData.isModerator || false,
            };

            sessionStorage.setItem(exchangeKey, 'done');
            setUser(realUser);
            sessionStorage.removeItem('reddit_oauth_state');
            deleteSharedCookie('reddit_oauth_state');
            const postLogin = getSharedCookie('post_login_redirect');
            deleteSharedCookie('post_login_redirect');
            if (postLogin) {
              window.location.href = postLogin;
              return;
            }
            navigate('/');
            return;
          }

          sessionStorage.removeItem(exchangeKey);
          console.error('Backend error:', response.status, errorBody);
          setError(formatLoginHttpError(response.status, errorBody));
          return;
        } catch (apiError) {
          sessionStorage.removeItem(exchangeKey);
          console.error('Error calling backend:', apiError);
          setError(
            `Could not reach the server (${apiError instanceof Error ? apiError.message : 'network error'}). Is /api proxied on this host?`
          );
          return;
        }
      } catch (err) {
        sessionStorage.removeItem(exchangeKey);
        console.error('Error handling OAuth callback:', err);
        setError('Failed to complete authentication');
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser]);

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please try logging in again.
        </Typography>
        <button onClick={() => navigate('/')}>
          Return to Home
        </button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
      <CircularProgress sx={{ mb: 2 }} />
      <Typography variant="h6">
        Completing authentication...
      </Typography>
    </Box>
  );
};

export default AuthCallback;
